import {inject, Injectable} from '@angular/core';
import {translate} from '@jsverse/transloco';
import {
  ImportanceScale,
  ProjectRoleListResponse,
  ProjectRoleType,
  ProjectStepType,
  ProjectUserMetadataControllerService,
  ProjectUserStatusType,
} from '@frontend/shared-api';
import {map, Observable, of} from 'rxjs';

export interface TranslatedOption<T> {
  translation: string;
  enum: T;
}

@Injectable({
  providedIn: 'root',
})
export class EnumTranslationMapperService {
  private readonly projectUserMetadataControllerService = inject(
    ProjectUserMetadataControllerService,
  );

  getAccessibleStepOptions(): Observable<TranslatedOption<ProjectStepType>[]> {
    return this.projectUserMetadataControllerService.getContributorAccessibleSteps().pipe(
      map((response) =>
        response.steps.map((step) => ({
          translation: this.getTranslationKeyForAccessibleStep(step),
          enum: step,
        })),
      ),
    );
  }

  private getTranslationKeyForAccessibleStep(step: ProjectStepType): string {
    const stepName = this.snakeToCamelCase(step);
    const translationKey = `accessibleStep.${stepName}`;

    return translate(translationKey);
  }

  private getTranslationKeyForImportanceScale(scale: ImportanceScale): string {
    const scaleName = this.snakeToCamelCase(scale);
    const translationKey = `project.steps.evaluationCriteria.importanceScale.${scaleName}`;

    return translate(translationKey);
  }

  getStatusLabel(status: ProjectUserStatusType): string {
    switch (status) {
      case ProjectUserStatusType.ACTIVE:
        return translate('userStatus.active');
      case ProjectUserStatusType.INACTIVE:
        return translate('userStatus.inactive');
      case ProjectUserStatusType.INVITED:
        return translate('userStatus.invited');
      case ProjectUserStatusType.CREATED:
        return translate('userStatus.created');
      default:
        return status;
    }
  }

  getAllRoleOptions(): Observable<TranslatedOption<ProjectRoleType>[]> {
    return this.projectUserMetadataControllerService.getAvailableRoles().pipe(
      map((response: ProjectRoleListResponse) =>
        response.roles.map((role: ProjectRoleType) => {
          const roleName = this.snakeToCamelCase(role);
          const translationKey = `role.${roleName}`;
          return {
            translation: translate(translationKey),
            enum: role,
          };
        }),
      ),
    );
  }

  getImportanceScaleOptions(): Observable<TranslatedOption<ImportanceScale>[]> {
    return of(
      Object.values(ImportanceScale).map((scale) => ({
        translation: this.getTranslationKeyForImportanceScale(scale),
        enum: scale,
      })),
    );
  }

  private snakeToCamelCase(str: string): string {
    return str
      .toLowerCase()
      .replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
  }
}
